import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Plus, Users, Calendar, Trash2, Mail, Phone, MapPin, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { ListDetailHeader } from "./ListDetailHeader";
import { ListProfile } from "./ListProfile";
import { ListTabs } from "./ListTabs";
import { AddCandidatesModal } from "./AddCandidatesModal";
import { AISearchModal } from "./AISearchModal";
import { StickyEditBanner } from "@/components/ui/StickyEditBanner";
import { useListDetail, useListMembers, useUpdateList, useDeleteList } from "@/hooks/lists/useListDetail";
import { useAddCandidatesToList } from "@/hooks/lists/useListDetail";
import { useToast } from "@/hooks/use-toast";
import { fetchCustomFields, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useAuth } from "@/providers/AuthProvider";
import { useTrackPageVisit } from "@/hooks/useEntityHistory";

export const ListDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [editList, setEditList] = useState<any>(null);
  const [showAddCandidatesModal, setShowAddCandidatesModal] = useState(false);
  const [showAISearchModal, setShowAISearchModal] = useState(false);
  const [activeTab, setActiveTab] = useState("members");
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const { currentOrganization } = useAuth();

  const { data: list, isLoading: isLoadingList, error: listError } = useListDetail(id!);
  const { data: members, isLoading: isLoadingMembers } = useListMembers(id!);

	usePageTitle(list ? list.name : "List");
	// Track page visit for history
	useTrackPageVisit('list', list?.id, list?.name, {
		candidateCount: members?.length || 0
	});  const updateListMutation = useUpdateList();
  const deleteListMutation = useDeleteList();
  const addCandidatesMutation = useAddCandidatesToList();

  useEffect(() => {
    if (list) {
      setEditList({
        name: list.name,
        description: list.description
      });
    }
  }, [list]);

  // Fetch custom fields configuration
  useEffect(() => {
    if (!currentOrganization?.id) return;
    setCustomFieldsLoading(true);
    fetchCustomFields(currentOrganization.id)
      .then((data: CustomFieldsJSON) => {
        setCustomFields(data.Lists || []);
      })
      .catch(() => {
        // Handle error silently for now
      })
      .finally(() => setCustomFieldsLoading(false));
  }, [currentOrganization?.id]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editList || !list) return;
    
    try {
      await updateListMutation.mutateAsync({
        id: list.id,
        updates: editList
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating list:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (list) {
      setEditList({
        name: list.name,
        description: list.description
      });
    }
  };

  const handleListChange = (field: string, value: string) => {
    setEditList(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomFieldsChange = (customFields: Record<string, any>) => {
    setEditList(prev => ({
      ...prev,
      custom_fields: customFields
    }));
  };

  const handleDelete = async () => {
    if (!list) return;
    
    try {
      await deleteListMutation.mutateAsync(list.id);
      navigate('/lists');
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  };

  const handleAddCandidates = async (candidateIds: string[]) => {
    if (!list) return;
    
    try {
      await addCandidatesMutation.mutateAsync({
        list_id: list.id,
        candidate_ids: candidateIds
      });
      setShowAddCandidatesModal(false);
    } catch (error) {
      console.error('Error adding candidates:', error);
    }
  };

  const handleAISearch = async (skills: string[]) => {
    if (!list) return;
    
    try {
      await addCandidatesMutation.mutateAsync({
        list_id: list.id,
        candidate_ids: [] // This would be populated by AI search results
      });
      setShowAISearchModal(false);
    } catch (error) {
      console.error('Error adding AI search results:', error);
    }
  };

  if (listError) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            {t("common.error", "Error")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t("lists.error.loading", "Failed to load list. Please try again.")}
          </p>
        </div>
      </div>
    );
  }

  if (isLoadingList || !list) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">{t("common.loading", "Loading...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ListDetailHeader
        isEditing={isEditing}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        onDelete={handleDelete}
        listName={list.name}
        listId={list.id}
      />
      
      {/* Ensure no space above ListProfile sticky banner */}
      <div className="p-0 m-0">
        <ListProfile
          list={isEditing ? editList! : list}
          isEditing={isEditing}
          onChange={setEditList}
          onSave={handleSave}
          onCancel={handleCancel}
          customFieldsConfig={customFields}
        />
      </div>

      <div>
        <ListTabs
          list={list}
          members={members}
          isLoadingMembers={isLoadingMembers}
          onMembersChange={() => {
            // This would trigger a refetch
          }}
          onAddCandidates={() => setShowAddCandidatesModal(true)}
          onAISearch={() => setShowAISearchModal(true)}
          refetchList={async () => {
            await useListDetail(id!).refetch();
          }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      <AddCandidatesModal
        isOpen={showAddCandidatesModal}
        onClose={() => setShowAddCandidatesModal(false)}
        onAddCandidates={handleAddCandidates}
        listId={list.id}
        isLoading={addCandidatesMutation.isPending}
      />

      <AISearchModal
        isOpen={showAISearchModal}
        onClose={() => setShowAISearchModal(false)}
        onAddCandidates={handleAISearch}
        listId={list.id}
        isLoading={addCandidatesMutation.isPending}
      />
    </div>
  );
};
